import { ReactElement, useState } from "react";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { ChatActionCard } from "@twiliointernal/e2-commons-shared-ui";
import { copy, WaitingInLineActionProps } from "./definitions";
import useSetWaitingInLineActions from "../../../../hooks/useSetWaitingInLineActions";
import SubmitTicketModalConfirmationAction from "./sub/submitTicketActions/SubmitTicketModalConfirmationAction";
import RequestNotification from "./sub/requestNotification/RequestNotification";
import { WiseOwlIngress } from "@twiliointernal/e2-commons-shared-definitions";
import { chatService } from "../../../../services/conversations";

/**
 * @NOTE
 * Current State
 *  - This component is specifically built for Live Chat usage in HCA.
 *  - Keeping all logics and definitions within this folder to easily move it out in case we want to
 *    de-couple from the "Waiting In Line" experience and allow the consumer of <LiveChat /> to implement
 *    "Waiting In Line" experience.
 * Future State
 *  - Add feature toggle on supported actions
 *  - We would like Live Chat should be app agnostic, but need to consider how much of freedom
 *    are we giving the consumer of LiveChat to build their own experience? For example:
 *    1. Should we only allow the consumer to only pass in the action handlers? or allow them
 *       to pass the whole component to customize the whole experience?
 *    2. Should we consider allowing the consumer to modify the assistant message?
 *    3. What should be the default state if the consumer doens't customize the "Waiting In Line" experience?
 */
const WaitingInLineActions = ({
  liveChatId,
  actions = {},
  ingress,
}: WaitingInLineActionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPhoneNotificationForm, setShowPhoneNotificationForm] =
    useState(false);

  const originalTicketSubmissionaction =
    actions.ticketSubmission?.handleActionButtonClick;

  const handleOpenModalSubmitTicket = () => {
    if (ingress === WiseOwlIngress.sendGrid) {
      chatService.endLiveChat();
      originalTicketSubmissionaction &&
        originalTicketSubmissionaction(actions.ticketSubmission?.params);
    } else {
      setIsModalOpen(true);
    }
  };
  const handleShowPhoneNotificationForm = () =>
    setShowPhoneNotificationForm(true);

  const handlers = {
    handleOpenModalSubmitTicket,
    handleShowPhoneNotificationForm,
  };

  const actionsToRender = useSetWaitingInLineActions(actions, handlers);

  const renderSubmitTicketModal = (): ReactElement => {
    return (
      <SubmitTicketModalConfirmationAction
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
        action={actions.ticketSubmission}
      />
    );
  };

  const renderPhoneNotificationForm = (): ReactElement => {
    return (
      <RequestNotification
        onClose={() => {
          setShowPhoneNotificationForm(false);
        }}
        liveChatId={liveChatId}
      />
    );
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        flexWrap={"wrap"}
        rowGap="space40"
        columnGap="space40"
        marginBottom="space80"
      >
        {actionsToRender.map(
          ({ label, icon, params, handleActionButtonClick }, index) => {
            return (
              <ChatActionCard
                key={`${index}-${label}`}
                heading={label}
                headingIcon={icon}
              >
                <Box paddingTop="space50">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleActionButtonClick && handleActionButtonClick(params)
                    }
                  >
                    {copy.actionButton.label}
                  </Button>
                </Box>
              </ChatActionCard>
            );
          }
        )}
      </Box>
      {isModalOpen && renderSubmitTicketModal()}
      {showPhoneNotificationForm && renderPhoneNotificationForm()}
    </>
  );
};

export default WaitingInLineActions;
